import { useState, useEffect, useContext } from "react";
import { DataStore, syncExpression } from '@aws-amplify/datastore'
import {
  DgtPdProductionOrder,
  DgtPdOrderOperation,
  DgtPdOrderOperationLog,
  DgtPdOrderReasonCode,
  DgtPdOrderPicklist, 
  DgtPdOrderPicklistMovement,
  DgtPdOrderPicklistExtraReturn,
  DgtPdOrderPicklistExtraReturnItem,
  DgtPdIAMUser
} from '../models'
import SyncExpressionContext from '../contexts/SyncExpressionContext'
import UserContext from '../contexts/UserContext'
import { AllowOrderStatus, DefaultOrderStatus } from "../constants/Enum";
import { getISOStartOfDate, getISOEndOfDate } from '../utilities/Format'
import { sub } from 'date-fns'

let localSyncOrderID
let localSyncOperationID
let localSyncOrderStatus
let localSyncPeriod

const EMPTY = 'N/A'

const DataStoreLayout = ({ children }) => {
  const { user } = useContext(UserContext)
  const [syncOrderID, setSyncOrderID] = useState(null);
  const [syncOperationID, setSyncOperationID] = useState(null);
  const [syncOrderStatus, setSyncOrderStatus] = useState(user && user.role ? DefaultOrderStatus[user.role] : '')

  const todayFormat = new Date()
  // const monthAgo = process.env.REACT_APP_ENV === 'PRD' ? 0 : 10
  const monthAgoFormat = process.env.REACT_APP_ENV === 'DEV' ? 
    sub(new Date(), { months: 1, days: 15 }) : sub(new Date(), { days: 30 })
  const [syncPeriod, setSyncPeriod] = useState([monthAgoFormat, todayFormat])

  const valueSync = {
    syncOrderID,
    setSyncOrderID,
    syncOperationID,
    setSyncOperationID,
    syncOrderStatus,
    setSyncOrderStatus,
    syncPeriod,
    setSyncPeriod
  };

  DataStore.configure({
    syncExpressions: [
      syncExpression(DgtPdProductionOrder, () => {
        if (localSyncOrderStatus) {
          const periodWithTimezone = [getISOStartOfDate(localSyncPeriod[0]), getISOEndOfDate(localSyncPeriod[1])]
          return order => order.statusFromApp('eq', localSyncOrderStatus).updatedAt('between', periodWithTimezone)
        } else {
          if (!['LeaderLines', 'Leaderline'].includes(user.role)) {
            // picklist zone : not sync pd order
            return order => order.statusFromApp('eq', EMPTY)
          }
          // all case
          return order => order.or(
            order => {
              let baseFilter
              AllowOrderStatus[user.role].forEach(role => {
                baseFilter = baseFilter ? baseFilter.statusFromApp('eq', role) : order.statusFromApp('eq', role)
              })
              return baseFilter
            }
          )
        }
      }),
      syncExpression(DgtPdOrderOperation, () => {
        if (localSyncOrderID) {
          return i => i.productionOrderId('eq', localSyncOrderID)
        } else {
          return i => i.productionOrderId('eq', EMPTY)
        }
      }),
      syncExpression(DgtPdOrderOperationLog, () => {
        if (localSyncOrderID && localSyncOperationID) {
          return i => i.productionOrderId('eq', localSyncOrderID).operationNo('eq', localSyncOperationID)
        } else {
          return i => i.productionOrderId('eq', EMPTY).operationNo('eq', EMPTY)
        }
      }),
      syncExpression(DgtPdOrderReasonCode, () => {
        if (localSyncOrderID) {
          return i => i.productionOrderId('eq', localSyncOrderID)
        } else {
          return i => i.productionOrderId('eq', EMPTY)
        }
      }),
      syncExpression(DgtPdOrderPicklist, () => {
        return p => p.pk('eq', '')
      }),
      syncExpression(DgtPdOrderPicklistMovement, () => {
        return p => p.orderId('eq', '')
      }),
      syncExpression(DgtPdOrderPicklistExtraReturn, () => {
        return p => p.id('eq', '')
      }),
      syncExpression(DgtPdOrderPicklistExtraReturnItem, () => {
        return p => p.id('eq', '')
      }),
      syncExpression(DgtPdIAMUser, () => {
        return p => p.id('eq', '')
      }),
    ],
    errorHandler: (error) => {
      console.warn("Unrecoverable error", { error });
    },
    maxRecordsToSync: 15000,
    fullSyncInterval: 60*24*3, // minutes - 3 days
  });

  useEffect(() => {
    const changeSync = async () => {
      localSyncOrderID = syncOrderID
      localSyncOperationID = syncOperationID
      await DataStore.stop()
      await DataStore.start()
    }
    changeSync()
  }, [ syncOrderID, syncOperationID ])

  useEffect(() => {
    const changeSync = async () => {
      localSyncOrderStatus = syncOrderStatus
      await DataStore.stop()
      await DataStore.start()
    }
    changeSync()
  }, [ syncOrderStatus ])

  useEffect(() => {
    const changeSync = async () => {
      localSyncPeriod = syncPeriod
      await DataStore.stop()
      await DataStore.start()
    }
    changeSync()
  }, [ syncPeriod ])

  return (
    <SyncExpressionContext.Provider value={valueSync} >
      {children}
    </SyncExpressionContext.Provider>
  )
}

export default DataStoreLayout